import moment from 'moment'
import { useQueryState } from 'nuqs'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import useSWR from 'swr'

import { Loading } from '@/components/Loading'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { DocumentForm } from '@/forms/DocumentForm'
import { Collection } from '@/interfaces/Collection'
import { Participant } from '@/interfaces/Participant'
import { Receipt } from '@invo.space/shared'
import { Settings } from '@/interfaces/Settings'
import API from '@/lib/api'

export default function InvoiceNewPage() {
  const navigate = useNavigate()
  const { t } = useTranslation(undefined, {
    keyPrefix: 'invoice',
  })
  const { t: tg } = useTranslation()
  const [customerId] = useQueryState('customerId')
  const { data: availableCustomers } = useSWR<Collection<Participant>>(
    `/api/v2/contacts`,
    apiFetcher,
  )
  const { data: settings, isLoading } = useSWR<Settings>(
    `/api/v2/settings`,
    apiFetcher,
  )
  const [receiptId] = useQueryState('receiptId')

  const { data: receipt, isLoading: isReceiptLoading } = useSWR<Receipt>(
    receiptId ? `/api/v2/receipts/${receiptId}` : null,
    apiFetcher,
  )

  const title = `${t('new.title')}`

  useSetPageHeader({
    breadcrumbs: [
      {
        href: '/invoices',
        children: t('list.title'),
      },
      {
        href: '/invoice/new',
        children: title,
      },
    ],
  })

  useEffect(() => {
    if (settings?.participantId === null) {
      toast.info(t('new.redirect.participantId'), {
        duration: 5000,
        dismissible: true,
        closeButton: true,
      })

      navigate('/settings/invoicing?ref=invoice')
    }
  }, [settings])

  if (isLoading || isReceiptLoading) {
    return <Loading />
  }

  let customer: Participant | undefined

  if (customerId) {
    customer = availableCustomers?.data?.find(
      (customer) => customer.id === customerId,
    )
  }

  return (
    <>
      <title>{title}</title>

      <DocumentForm
        type="invoice"
        settings={settings!}
        documentData={{
          customer: customer,
          items: receipt?.receiptLines.map((line) => ({
            amount: line.amount,
            unit: 'x',
            description: line.product?.name || '',
            taxRate: settings?.vatPayer === 'vatPayer' ? line.taxRate : undefined,
            unitPriceTaxFree: line.unitPriceTaxFree,
          })),
          issuedAt: receipt?.issuedAt,
          taxableAt: receipt?.issuedAt,
          deadlineAt: receipt?.issuedAt,
          paidAt: receipt?.issuedAt,
          description: receipt ? tg('documentForm.description.assignedToReceipt', { symbol: moment(receipt!.issuedAt).format('YYYYMMDDHHmmss') }) : undefined,
        }}
        onSave={(document) => {

          new Promise((resolve) => {
            if (receipt) {
              return API.post(`/api/v2/references`, {
                source: receipt.id,
                sourceType: 'receipt',
                target: document.id,
                targetType: 'invoice',
              })
              .then(() => {
                resolve(null)
              })
              .catch((error) => {
                console.error(error)
                resolve(null)
              })
            }

            return resolve(null)
          }).finally(() => {
            navigate(`/invoice/${document.symbol}`)
          })
        }}
      />
    </>
  )
}
